<template>
  <div class="charidy-app">
    <category-title :title="$t('charidy_app.page_title', 'Charidy App')" />

    <div v-if="loading && !loaded" class="loading">
      <app-spinner />
    </div>

    <b-alert v-else-if="loadFailed" show variant="danger" class="charidy-app__alert">
      {{ $t("charidy_app.load_error", "We could not load your Charidy App registration.") }}
      <button class="charidy-app__retry" @click="fetchCharidyAppStatus">
        {{ $t("charidy_app.load_error_retry", "Try again") }}
      </button>
    </b-alert>

    <div v-else-if="status.enrolled" class="charidy-app__card">
      <b-alert show variant="success" class="charidy-app__alert">
        <p class="charidy-app__subtitle">
          {{
            $t(
              "charidy_app.registered_title",
              "Your organization is registered with the Charidy app"
            )
          }}
        </p>
        {{
          $t(
            "charidy_app.registered_body",
            "Donations made to your organization through the Charidy app are recorded on the campaign below."
          )
        }}
      </b-alert>

      <div v-if="campaign" class="charidy-app__campaign">
        <div>
          <div class="charidy-app__campaign-title">
            {{ campaign.campaigntitle || $t("charidy_app.campaign_untitled", "General donations") }}
          </div>
          <div class="charidy-app__campaign-meta">
            #{{ campaign.campaign_id }}
            <template v-if="campaign.currency"> · {{ campaign.currency.toUpperCase() }}</template>
          </div>
        </div>
        <router-link
          :to="`/campaign/${campaign.campaign_id}`"
          class="bttn bttn--sm bttn--bordered-blue"
        >
          {{ $t("charidy_app.campaign_open", "Open campaign") }}
        </router-link>
      </div>

      <p>
        {{
          $t(
            "charidy_app.how_edit",
            "You can update the organization details shown in the app at any time."
          )
        }}
        <router-link :to="orgProfilePath">
          {{ $t("charidy_app.how_edit_link", "Edit organization details") }}
        </router-link>
      </p>
    </div>

    <div v-else class="charidy-app__card">
      <p class="charidy-app__title">
        {{ $t("charidy_app.register_title", "Accept donations through the Charidy app") }}
      </p>

      <p>
        {{
          $t(
            "charidy_app.intro",
            "The Charidy Wallet app lists organizations that have approved receiving donations through this channel."
          )
        }}
      </p>

      <p class="charidy-app__subtitle">
        {{ $t("charidy_app.how_title", "How it works") }}
      </p>
      <p>
        {{
          $t(
            "charidy_app.how_body",
            "We set up a campaign for your organization, and every donation made through the app is recorded on it."
          )
        }}
      </p>
      <p v-if="currency">
        {{
          $t("charidy_app.how_currency", "Donations from the app are collected in {currency}.", {
            currency: currency.toUpperCase(),
          })
        }}
      </p>
      <p>
        {{
          $t(
            "charidy_app.how_edit",
            "You can update the organization details shown in the app at any time."
          )
        }}
        <router-link :to="orgProfilePath">
          {{ $t("charidy_app.how_edit_link", "Edit organization details") }}
        </router-link>
      </p>

      <b-alert v-if="!currency" show variant="warning" class="charidy-app__alert">
        <p class="charidy-app__subtitle">
          {{ $t("charidy_app.no_gateway_title", "Connect a payment gateway first") }}
        </p>
        {{
          $t(
            "charidy_app.no_gateway_body",
            "Donations from the app are charged through one of your payment gateways, so you need an active gateway before you can register."
          )
        }}
        <router-link to="/gateways">
          {{ $t("charidy_app.no_gateway_action", "Payment gateways") }}
        </router-link>
      </b-alert>

      <template v-else>
        <label class="charidy-app__consent">
          <input v-model="accepted" type="checkbox" :disabled="submitting" />
          <span>
            {{ $t("charidy_app.consent_label", "I accept the") }}
            <a v-if="termsUrl" :href="termsUrl" target="_blank" rel="noopener noreferrer">
              {{ $t("charidy_app.terms_link", "terms of use") }}
            </a>
            <span v-else>{{ $t("charidy_app.terms_link", "terms of use") }}</span>
          </span>
        </label>

        <div class="charidy-app__actions">
          <button
            class="bttn bttn--sm bttn--orange"
            :disabled="!accepted || submitting"
            @click="register"
          >
            {{ $t("charidy_app.register", "Register") }}
          </button>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import categoryTitle from "@/common-components/ui-elements/category-title.vue";
import appSpinner from "@/common-components/ui-elements/app-spinner.vue";
import { notificationsMixin } from "@/mixins";

/**
 * Where an organization registers to accept donations through the Charidy app,
 * and later sees the campaign those donations land on.
 *
 * Three states, all decided by the backend: registered (show the campaign),
 * not registered with a gateway to be paid in (explain, and offer the terms and
 * the register button), and not registered without a gateway (point at the
 * gateways page first — registering would have nothing to bill through).
 */
export default {
  name: "CharidyAppPage",

  mixins: [notificationsMixin],

  components: {
    categoryTitle,
    appSpinner,
  },

  data() {
    return {
      accepted: false,
      // Set once the first read finishes, so a re-read after registering keeps
      // the page on screen instead of flashing the spinner.
      loaded: false,
      // Where the org edits the details the app shows about it.
      orgProfilePath: "/account",
    };
  },

  computed: {
    ...mapGetters({
      status: "getCharidyAppStatus",
      loading: "getLoadingCharidyApp",
      loadFailed: "getLoadCharidyAppFailed",
      submitting: "getSubmittingCharidyApp",
    }),

    campaign() {
      return this.status.campaign || null;
    },

    termsUrl() {
      return this.status.terms_url || "";
    },

    // What the app's donations will be billed in: the org's language preference
    // where it has a matching gateway, its first gateway otherwise. Empty when it
    // has no gateway, which is when it cannot register yet.
    currency() {
      return this.status.currency || "";
    },
  },

  mounted() {
    this.fetchCharidyAppStatus().finally(() => {
      this.loaded = true;
    });
  },

  methods: {
    ...mapActions(["fetchCharidyAppStatus", "registerCharidyApp"]),

    register() {
      if (!this.accepted || this.submitting) {
        return;
      }

      this.registerCharidyApp()
        .then(() => {
          this.$_notificationsMixin_makeToast(
            this.$t("charidy_app.register_success_title", "You're all set"),
            this.$t(
              "charidy_app.register_success_body",
              "Your organization now appears in the Charidy app, and donations made there will be recorded on your general donations campaign."
            ),
            "success"
          );
        })
        .catch(() => {
          this.$_notificationsMixin_makeToast(
            this.$t("common.error", "Error"),
            this.$t(
              "charidy_app.register_error",
              "We could not complete the registration. Please try again."
            ),
            "danger"
          );
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.charidy-app {
  &__card {
    max-width: 720px;
    padding: 20px;
    background-color: #ffffff;
    border-radius: 4px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.05);

    p {
      margin-bottom: 10px;
    }
  }

  &__title {
    font-size: 18px;
    font-weight: 600;
  }

  &__subtitle {
    font-weight: 600;
    margin-bottom: 4px !important;
  }

  &__alert {
    max-width: 720px;
  }

  &__retry {
    background: none;
    border: none;
    padding: 0;
    margin-left: 6px;
    color: inherit;
    text-decoration: underline;
  }

  &__campaign {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 16px;
    padding: 12px 16px;
    border: 1px solid rgba(32, 67, 78, 0.15);
    border-radius: 4px;
  }

  &__campaign-title {
    font-weight: 600;
  }

  &__campaign-meta {
    font-size: 13px;
    color: #6c757d;
  }

  &__consent {
    display: flex;
    align-items: flex-start;
    gap: 6px;
    margin: 0;

    input {
      margin-top: 4px;
    }
  }

  &__actions {
    display: flex;
    justify-content: flex-end;
    margin-top: 16px;
  }
}
</style>
